import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import SellUsedCar from "../sell-used-car";
import MetaTags from "../../../components/shared/meta-tags";
import { CITY_KEY, seoTemplateType, HTTP_CODES, STEP_NAMES, LEAD_FORM, NUMBER } from "../../../constants/app-constants";
import { setItem } from "../../../utils/helpers/storage-crud";
import { findByKey } from "../../../utils/helpers/find-by-key";
import ErrorHandler from "../../../components/shared/with-error-handler";
import { FUNNEL_CONFIG } from "../../../utils/funnel/steps.desktop";
import SellUsedCarCityList from "../../shared/sell-used-car-city-list";
import updateCurrentCity from "../../../utils/helpers/set-current-city";
import EventHandler from "../../../utils/event-handler";
import { emitEventNames } from "../../../constants/event-constants";
import saveCookie from "../../../utils/helpers/save-cookie";
import loadable from "@loadable/component";
import HomeBanner from "../home-banner";
import { LazyComponent } from "../../shared/lazy-image";
import { sendCustomGaEvents } from "../../../tracking";
import { buyCar, scrollStoresAndParkingYards, getCarPriceBottom, popularCityTextClick } from "./tracking";
import { breadcrumbClicked } from "../../../constants/tracking-constants/breadcrumb";
import { appUrl } from "../../../constants/url-constants";
import BranchesHubsCarousel from "../../shared/branches-hubs-carousel";
import { HOMEPAGE_TYPES } from "../../../constants/seller-constant";
import BreadcrumbList from "../../shared/breadcrumb-list";
import PilotFaq from "../../../components/desktop/pilot-faq";
import GenericBrandBanner from "../../shared/generic-brand-banner";
import useC2bCleverTap from "../../../hooks/use-c2b-clevertap";

const NotFound = loadable(() => import("../../../components/shared/page-not-found"));
const WhyCars = loadable(() => import("../../../components/desktop/why-cars"));
const SellEasySteps = loadable(() => import("../../../components/desktop/sell-easy-steps"));
const SimplestWay = loadable(() => import("../simplest-way"));
const TestimonialCarouselRevamp = loadable(() => import("../../../components/desktop/testimonial-carousel-revamp"));
const ExchangeBanner = loadable(() => import("../exchange-bonus"));

// eslint-disable-next-line max-statements, complexity
const SellUsedCarSeo = ({
    list: reviewList,
    identifiers,
    templateType = "",
    allBrands,
    allModels,
    cityList,
    status,
    isFirstLoad,
    match,
    selectCityConnect,
    resetLeadFormDataConnect,
    setFirstLoadConnect,
    updateLeadSourceConnect,
    setDataConnect,
    setStepConnect,
    nearestCities,
    hubsAndParkingYards,
    isSSR,
    fetchDataConnect,
    mainHeading,
    faqList,
    topCitiesList,
    statsigDynamicConfigData,
    websiteAdvertisementBannerData
}) => {
    useC2bCleverTap("Sell_car");
    const [faqs, setFaqs] = useState([]);
    const [hasReset, setHasReset] = useState(false);
    const cityId = identifiers && identifiers.city && identifiers.city.toString();
    const {
        city_name: cityName,
        background_image: backgroundImage,
        is_buy_used_car: isBuyUsedCar,
        buy_used_car_url: buyUsedCardUrl,
        buy_used_car_url_sec_cta: viewAllCarsUrl,
        pincode: selectedPincode,
        city_slug: citySlug
    } = findByKey(cityList, "city_id", cityId);
    const {  websiteAds = {}} = statsigDynamicConfigData || {};
    const memoizedTopCitiesList = useMemo(() => {
        if (topCitiesList && topCitiesList.length) return topCitiesList.slice(NUMBER.ZERO, NUMBER.TWENTY);
        return null;
    }, [topCitiesList]);
    useEffect(() => {
        if (!isSSR) {
            const city = match && match.params && match.params.city;
            const cmsPayload = {
                cmsuri: `/${city}`,
                expand: "make_list,seo_content,city_list,customer_review_list,tweets"
            };
            fetchDataConnect(cmsPayload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSSR]);

    useEffect(() => {
        updateLeadSourceConnect("seo-page");
    }, [updateLeadSourceConnect]);

    useEffect(() => {
        if (isFirstLoad && ![seoTemplateType.CITY, seoTemplateType.CITY_LOCATION,
            "", seoTemplateType.GENERIC, undefined]
            .includes(templateType)) {
            setFirstLoadConnect();
            resetLeadFormDataConnect();
            setHasReset(true);
        }
    }, [isFirstLoad, resetLeadFormDataConnect, setFirstLoadConnect, templateType]);
    useEffect(() => {
        if (hasReset && identifiers) {
            if (identifiers.make) {
                const carBrand = findByKey(allBrands, "make_id", identifiers.make);
                setDataConnect({
                    type: STEP_NAMES.Brand, data: {
                        displayText: carBrand.make_display, id: identifiers.make
                    }
                }, FUNNEL_CONFIG[LEAD_FORM.DEFAULT]);
                setStepConnect({
                    currentStep: STEP_NAMES.Model,
                    tabs: FUNNEL_CONFIG[LEAD_FORM.DEFAULT].tabs
                });
            }
            if (identifiers.model) {
                const carModel = findByKey(allModels, "model_id", identifiers.model);
                setDataConnect({
                    type: STEP_NAMES.Model, data: {
                        displayText: carModel.model_display, id: identifiers.model
                    }
                }, FUNNEL_CONFIG[LEAD_FORM.DEFAULT]);
                setStepConnect({
                    currentStep: STEP_NAMES.Year,
                    tabs: FUNNEL_CONFIG[LEAD_FORM.DEFAULT].tabs
                });
            }
        }
    }, [allBrands, allModels, hasReset, identifiers, setDataConnect, setStepConnect]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (templateType && templateType.toLowerCase().includes(seoTemplateType.CITY)) {
            const parsedCityList = cityList.filter((item) => !item.is_virtual_city);

            saveCookie("pincode", selectedPincode);
            updateCurrentCity(
                parsedCityList,
                parseInt(cityId),
                selectCityConnect,
                true
            );
            EventHandler.getInstance().emitEvent(
                emitEventNames.mobile.ON_CITY_SELECTION,
                {
                    id: cityId,
                    name: cityName,
                    image: backgroundImage,
                    url: buyUsedCardUrl,
                    viewAllCarsUrl
                }
            );
            setItem(
                {
                    id: cityId,
                    name: cityName,
                    image: backgroundImage,
                    url: isBuyUsedCar && buyUsedCardUrl,
                    viewAllCarsUrl
                }, CITY_KEY);
            selectCityConnect(
                cityId,
                cityName,
                backgroundImage,
                isBuyUsedCar && buyUsedCardUrl,
                isBuyUsedCar && viewAllCarsUrl
            );
        }
    }, [templateType, identifiers, cityList, selectCityConnect, selectedPincode, cityName, backgroundImage, buyUsedCardUrl, viewAllCarsUrl, isBuyUsedCar, cityId]);
    // const sellCarText = {
    //     header: "Ready to Sell Your Car?",
    //     description: `You've made it all the way down here,
    //     now all it takes is a single visit to
    //     CARS24 branch to sell car without any RC transfer hassle.`
    // };

    if (status === HTTP_CODES.NOT_FOUND) {
        return <NotFound />;
    }

    const getHeading = () => {
        if (cityName) return `${cityName}`;
        return "";
    };

    const onClick = () => {
        sendCustomGaEvents(getCarPriceBottom);
        window.scrollTo(0, 0);
    };

    const redirect = () => {
        sendCustomGaEvents(buyCar);
        window.location.href = `/buy-used-cars-${citySlug}`;
    };

    const callBack = (popularCityName) => {
        sendCustomGaEvents({
            ...popularCityTextClick,
            eventLabel: popularCityName
        });
    };

    const onBreadcrumbClick = ({ item }) => {
        if (item === "home") {
            sendCustomGaEvents({
                ...breadcrumbClicked,
                eventLabel: item
            });
            window.location.href = `${appUrl()}/`;
        } else {
            sendCustomGaEvents(breadcrumbClicked);
            window.location.href = `${appUrl()}${item.redirectURL}`;
        }
    };

    const computeBreadcrumbList = () => {
        const list = [{
            displayText: "Sell car",
            redirectURL: "/sell-used-cars/"
        }];

        if (cityName) {
            list.push({
                displayText: `Sell car in ${cityName}`
            });
        }
        return list;
    };

    const scrollHubsGaCallback = () => {
        sendCustomGaEvents(scrollStoresAndParkingYards);
    };

    return (
        <div>
            <ErrorHandler>
                <MetaTags match={match} attacheWebPageSchema/>
            </ErrorHandler>

            <div styleName={"styles.buyCarsVariant"}>
                <HomeBanner
                    heading={getHeading()}
                    homePageType={HOMEPAGE_TYPES.CITY_PAGE}
                    mainHeading={mainHeading}
                />
            </div>
            { websiteAdvertisementBannerData &&   <div styleName={"styles.adsBanner"}><GenericBrandBanner config={websiteAds} type={"970x90"} fetchpriority="high" pageType={`sell-used-car-${cityName}`}  position = "top" source = "webApp"/></div>}
            <div styleName={"styles.wrapper"}>
                <LazyComponent>
                    <ErrorHandler>
                        <SellUsedCar />
                    </ErrorHandler>
                </LazyComponent>

                <LazyComponent>
                    <ErrorHandler>
                        <SellEasySteps />
                    </ErrorHandler>
                </LazyComponent>

                <LazyComponent>
                    <ErrorHandler>
                        <WhyCars faqs={faqs} setFaqs={setFaqs} slug={"city-seo"} />
                    </ErrorHandler>
                </LazyComponent>
            </div>
            <ErrorHandler>
                {!!(reviewList && reviewList.length >= NUMBER.TWO) && <div styleName={"styles.twitterCarousel"}>
                    <LazyComponent>
                        <TestimonialCarouselRevamp showImg={false} currentPage={"home"} showDate={false}/>
                    </LazyComponent>
                </div>}
            </ErrorHandler>

            <LazyComponent>
                <ExchangeBanner isBuyUsedCar={isBuyUsedCar} onClick={redirect}/>
            </LazyComponent>

            { hubsAndParkingYards && !!hubsAndParkingYards.length &&
                <div styleName={"styles.branchesHubsCarouselWrapper"}>
                    <BranchesHubsCarousel
                        slidesToShow={3}
                        hubsList={hubsAndParkingYards}
                        scrollWidgetGa={scrollHubsGaCallback}
                    />
                </div>
            }

            {
                !!(nearestCities && nearestCities.length) &&
                <div styleName={"styles.cityWrap"}>
                    <ErrorHandler>
                        <SellUsedCarCityList
                            cityList={nearestCities}
                            callBack={callBack}
                            heading={"Nearby Cities to Sell your Car"}
                        />
                    </ErrorHandler>
                </div>
            }

            {
                !!(memoizedTopCitiesList && memoizedTopCitiesList.length) &&
                <div styleName={"styles.cityWrap styles.popularCities"}>
                    <ErrorHandler>
                        <SellUsedCarCityList
                            cityList={memoizedTopCitiesList}
                            callBack={callBack}
                            heading={"Popular Cities to Sell Your Car"}
                        />
                    </ErrorHandler>
                </div>
            }
            {
                !!(faqList && faqList.length) &&
                <div styleName={"styles.faqWrapper"}>
                    <PilotFaq heading={"Frequently asked questions"} faqs={faqList} columns={1} attachSchema={true} hasListElements/>
                    <a href="/faq/" target="_blank" styleName="styles.allfqa">view all faqs</a>
                </div>
            }

            <LazyComponent>
                <SimplestWay onClick={onClick} />
            </LazyComponent>
            <BreadcrumbList
                homeText={"Home"}
                onClickCallback={onBreadcrumbClick}
                list={computeBreadcrumbList()}
                isRevamped = {true}
                attachSchema={true}
            />
        </div>
    );
};

SellUsedCarSeo.propTypes = {
    identifiers: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    templateType: PropTypes.string.isRequired,
    selectCityConnect: PropTypes.func.isRequired,
    cityList: PropTypes.array.isRequired,
    status: PropTypes.number,
    image: PropTypes.string,
    resetLeadFormDataConnect: PropTypes.func,
    isFirstLoad: PropTypes.bool,
    setFirstLoadConnect: PropTypes.func,
    updateLeadSourceConnect: PropTypes.func,
    allBrands: PropTypes.array,
    allModels: PropTypes.array,
    setDataConnect: PropTypes.func,
    setStepConnect: PropTypes.func,
    showCityList: PropTypes.bool,
    nearestCities: PropTypes.array,
    list: PropTypes.array,
    hubsAndParkingYards: PropTypes.array,
    isSSR: PropTypes.bool,
    fetchDataConnect: PropTypes.func,
    mainHeading: PropTypes.string,
    faqList: PropTypes.array,
    topCitiesList: PropTypes.array,
    statsigDynamicConfigData: PropTypes.object,
    websiteAdvertisementBannerData: PropTypes.bool
};

export default SellUsedCarSeo;
